import { WalletOutlined, CopyOutlined } from "@ant-design/icons";
import {
  AutoComplete,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  TreeSelect,
  Select,
  Tabs,
  Upload,
} from "antd";
import {
  useContext,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { API } from "src/shared";
import { NomenclatureContext } from "src/shared/lib/hooks/context/getNomenclatureContext";

import debounce from "lodash.debounce";
import { apiRequests } from "src/shared/api/api";

const formItemLayout = { labelCol: { span: 5 }, wrapperCol: { span: 17 } };

const Nomenclature = forwardRef(
  (
    {
      formContext,
      record,
      handleSaveImage,
      handleDeleteImage,
      setChangeData,
      withoutImage = false,
    },
    ref
  ) => {
    const [messageApi, contextHolder] = message.useMessage();
    const {
      manufacturersData = [],
      categoriesData = [],
      unitsData = [],
      token,
    } = useContext(NomenclatureContext);
    const [manufacturers, setManufacturers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [pictures, setPictures] = useState([]);
    const [units, setUnits] = useState([]);
    const [nomenclature, setNomenclature] = useState([]);
    const [optionsName, setOptionsName] = useState([]);
    const [optionsLocation, setOptionsLocations] = useState([]);
    // legacy (плоский список) больше не используется
    // const [globalCategories, setGlobalCategories] = useState([]);
    const [cashbackType, setCashbackType] = useState(
      record && record.cashback_type ? record.cashback_type : null
    );
    const [activeTab, setActiveTab] = useState("main");
    const [globalCategoriesTree, setGlobalCategoriesTree] = useState([]);

    useImperativeHandle(ref, () => ({
      setActiveTab: (tabKey) => {
        setActiveTab(tabKey);
      },
    }));

    const types = [
      { value: "product", label: "Товар" },
      { value: "service", label: "Услуга" },
      { value: "offer", label: "Предложение" },
      { value: "resourse", label: "Ресурс" },
      { value: "rental", label: "Аренда" },
      { value: "property", label: "Недвижимость" },
      { value: "work", label: "Работа" },
    ];

    const { TextArea } = Input;

    const getNomenclature = API.crud.get(token, "/nomenclature");

    const validateMessages = {
      /* eslint-disable no-template-curly-in-string */
      required: "Поле обязательно для ввода!",
    };

    // Загрузка дерева глобальных категорий для TreeSelect
    useEffect(() => {
      const loadGlobalCategoriesTree = async () => {
        try {
          const res = await fetch(
            `https://${process.env.REACT_APP_APP_URL}/api/v1/mp/categories/tree-select/`,
          );
          const data = await res.json();
          if (data?.result) {
            setGlobalCategoriesTree(data.result);
          }
        } catch (err) {
          console.error("Ошибка загрузки дерева глобальных категорий:", err);
        }
      };
      loadGlobalCategoriesTree();
    }, []);

    // Автозаполнение адреса
    const onChangeLocation = async (value) => {
      if (!value) {
        setOptionsLocations([]);
        return;
      }
      try {
        const res = await fetch(
          `https://${process.env.REACT_APP_APP_URL}/api/v1/autosuggestions/geolocation?query=${encodeURIComponent(
            value,
          )}&limit=5`,
        );
        const data = await res.json();
        if (data?.suggestions?.length) {
          setOptionsLocations(
            data.suggestions.map((suggest) => ({
              value: suggest,
              label: suggest,
            })),
          );
        } else {
          setOptionsLocations([
            {
              value: value,
              label: value,
            },
          ]);
        }
      } catch (err) {
        console.error("Ошибка загрузки адресов:", err);
      }
    };

    const debounceOnChangeLocation = debounce(onChangeLocation, 500);

    // Получение координат для выбранного адреса
    const handleAddressSelect = async (address) => {
      formContext.setFieldsValue({ address: address });

      // Получаем координаты для выбранного адреса
      try {
        const res = await fetch(
          `https://${process.env.REACT_APP_APP_URL}/api/v1/autosuggestions/geolocation/validate?address=${encodeURIComponent(
            address,
          )}`,
        );
        const data = await res.json();

        if (data?.latitude !== undefined && data?.longitude !== undefined) {
          formContext.setFieldsValue({
            latitude: data.latitude,
            longitude: data.longitude,
          });
        }
      } catch (err) {
        console.error("Ошибка получения координат:", err);
      }
    };

    const onChangeName = (value) => {
      formContext.setFieldsValue({ name: value }); // Update form value directly
      setOptionsName(
        nomenclature.filter((n) =>
          n.value.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    };

    const debounceOnChangeName = debounce(onChangeName, 500);

    useEffect(() => {
      if (unitsData.length !== 0) {
        const unitsSelect = [];
        for (let item of unitsData) {
          unitsSelect.push({ value: item.id, label: item.name });
        }
        setUnits(unitsSelect);
      }
      if (manufacturersData.length !== 0) {
        const manufacturersSelect = [];
        for (let item of manufacturersData) {
          manufacturersSelect.push({ value: item.id, label: item.name });
        }
        setManufacturers(manufacturersSelect);
      }
      if (categoriesData.length !== 0) {
        const categoriesSelect = [];
        for (let item of categoriesData) {
          categoriesSelect.push({ value: item.id, label: item.name });
        }
        setCategories(categoriesSelect);
      }
      if (record?.pictures !== undefined && record?.pictures.length !== 0) {
        const picturesDefault = [];
        for (let item of record.pictures) {
          picturesDefault.push({
            id: item.id,
            name: item.url,
            status: "done",
            url: `https://${process.env.REACT_APP_APP_URL}/api/v1/${item.url}/`,
          });
        }
        setPictures(picturesDefault);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manufacturersData, categoriesData, unitsData]);

    useEffect(() => {
      const initial = async () => {
        await getNomenclature().then((res) =>
          setNomenclature(res.result.map((el) => ({ value: el.name }))),
        );
      };

      if (!nomenclature.length) {
        initial();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (record?.id) {
        apiRequests.pictures
          .getItems({
            entity: "nomenclature",
            entity_id: record.id,
          })
          .then((response) => {
            setPictures(
              response.data.result.map((item) => ({
                id: item.id,
                name: item.url,
                status: "done",
                url: `https://${process.env.REACT_APP_APP_URL}/api/v1/${item.url}/`,
              })),
            );
          });
      }
    }, [record?.id]);

    const filterOption = (input, option) =>
      (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

    const onValuesChange = () => {
      setChangeData(true);
    };

    // Обновляем значения формы при изменении record
    useEffect(() => {
      if (record) {
        // Устанавливаем все значения из record, включая qr_hash
        formContext.setFieldsValue(record);
      }
    }, [record, formContext]);

    return (
      <>
        {contextHolder}
        <Form
          {...formItemLayout}
          form={formContext}
          initialValues={record}
          onValuesChange={onValuesChange}
          layout={"horizontal"}
          style={{ maxWidth: "100%" }}
          validateMessages={validateMessages}
          scrollToFirstError
        >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "main",
              label: "Основное",
              children: (
                <>
                  <Form.Item
                    rules={[{ required: true }]}
                    label={"Имя"}
                    name={"name"}
                  >
                    <AutoComplete
                      options={optionsName}
                      onSearch={debounceOnChangeName}
                      onChange={(value) => {
                        formContext.setFieldsValue({ name: value });
                      }}
                      filterOption={false}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item label={"Тип"} name={"type"}>
                    <Select placeholder="Выберите тип">
                      {types.map((t) => (
                        <Select.Option key={t.value} value={t.value}>
                          {t.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label="Теги" name="tags">
                    <Select
                      mode="tags"
                      style={{ width: "100%" }}
                      tokenSeparators={[","]}
                    />
                  </Form.Item>
                  <Form.Item label={"Краткое описание"} name={"description_short"}>
                    <TextArea rows={4} />
                  </Form.Item>
                  <Form.Item label={"Длинное описание"} name={"description_long"}>
                    <TextArea rows={4} />
                  </Form.Item>
                  <Form.Item label={"Код"} name={"code"}>
                    <Input />
                  </Form.Item>
                  <Form.Item label={"Единица измерения"} name={"unit"}>
                    <Select filterOption={filterOption} showSearch options={units} />
                  </Form.Item>
                  <Form.Item label={"Категория"} name={"category"}>
                    <Select
                      filterOption={filterOption}
                      showSearch
                      options={categories}
                    />
                  </Form.Item>
                  <Form.Item label={"Производитель"} name={"manufacturer"}>
                    <Select
                      filterOption={filterOption}
                      showSearch
                      options={manufacturers}
                    />
                  </Form.Item>
                  <Form.Item label={"Тип кешбека"} name={"cashback_type"}>
                    <Select value={cashbackType} onChange={setCashbackType}>
                      <Select.Option value={"percent"}>Проценты</Select.Option>
                      <Select.Option value={"const"}>Постоянный</Select.Option>
                      <Select.Option value={"lcard_cashback"}>
                        По карте лояльности
                      </Select.Option>
                      <Select.Option value={"no_cashback"}>Отсутствует</Select.Option>
                    </Select>
                  </Form.Item>
                  {cashbackType === "percent" || cashbackType === "const" ? (
                    <Form.Item label={"Кешбек"} name={"cashback_value"}>
                      <Input
                        placeholder={
                          cashbackType === "percent"
                            ? "В процентах"
                            : cashbackType === "const"
                              ? "В баллах"
                              : ""
                        }
                        addonAfter={
                          cashbackType === "percent" ? "%" : <WalletOutlined />
                        }
                      />
                    </Form.Item>
                  ) : null}
                  {!withoutImage ? (
                    <Form.Item label={"Изображение"}>
                      <Upload
                        action={`https://${process.env.REACT_APP_APP_URL}/api/v1/pictures/?token=${token}&entity=nomenclature&entity_id=${record.id}`}
                        fileList={pictures}
                        listType="picture-card"
                        onChange={({
                          fileList: newFileList,
                          file: newFile,
                        }) => {
                          if (newFile.status === "done") {
                            handleSaveImage(newFile.response);
                            setPictures(newFileList);
                            return message.success(
                              "Изображение было добавлено",
                            );
                          }
                          setPictures(newFileList);
                        }}
                        onRemove={(picture) => {
                          if (picture.response !== undefined) {
                            return handleDeleteImage(picture.response.id);
                          }
                          handleDeleteImage(picture.id);
                        }}
                      >
                        <Button>Загрузить</Button>
                      </Upload>
                      {/* <div style={{padding: '10px 0', display: 'flex', gap: 10}}>
                  {
                    pics.map(pic => <PreviewImage items={[pic]} width={60} height={60} key={pic.id} />)
                  }
                </div> */}
                    </Form.Item>
                  ) : null}
                </>
              ),
            },
            {
              key: "seo",
              label: "SEO",
              children: (
                <>
                  <Form.Item label={"SEO Title"} name={"seo_title"}>
                    <Input placeholder="Заголовок для поисковиков" />
                  </Form.Item>

                  <Form.Item label={"SEO Description"} name={"seo_description"}>
                    <TextArea
                      rows={3}
                      placeholder="Краткое описание для поисковиков"
                    />
                  </Form.Item>

                  <Form.Item label={"SEO Keywords"} name={"seo_keywords"}>
                    <Select
                      mode="tags"
                      style={{ width: "100%" }}
                      tokenSeparators={[","]}
                      placeholder="Ключевые слова"
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: "quick",
              label: "Быстро",
              children: (
                <>
                  <Form.Item
                    label={"Глобальная категория"}
                    name={"global_category_id"}
                  >
                    <TreeSelect
                      style={{ width: "100%" }}
                      treeData={globalCategoriesTree}
                      placeholder="Выберите глобальную категорию"
                      treeDefaultExpandAll={false}
                      showSearch
                      allowClear
                      treeLine
                      filterTreeNode={(input, node) =>
                        (node?.title || "").toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label={"Цена для маркетплейса"}
                    name={"marketplace_price"}
                    tooltip="Цена товара для отображения на маркетплейсе"
                    normalize={(value) => (value === "" ? undefined : value)}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={0.01}
                      precision={2}
                      placeholder="0.00"
                      addonAfter="₽"
                    />
                  </Form.Item>

                  <Form.Item
                    label={"Комиссия маркета"}
                    name={"chatting_percent"}
                    tooltip="Процент комиссии маркетплейса (4-100), округляется кратно 4%"
                    extra="Минимум 4%. Значение приводится к ближайшему кратному 4% (4–100)"
                    normalize={(value) => (value === "" ? undefined : value)}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={4}
                      max={100}
                      step={4}
                      precision={0}
                    placeholder="4–100"
                      addonAfter="%"
                      onBlur={() => {
                        const current = formContext.getFieldValue("chatting_percent");
                        if (current === null || current === undefined) return;
                        const rounded = Math.round(Number(current) / 4) * 4;
                        const clamped = Math.min(100, Math.max(4, rounded));
                        if (clamped !== current) {
                          formContext.setFieldsValue({ chatting_percent: clamped });
                        }
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={"QR-hash"}
                    name={"qr_hash"}
                    tooltip="QR-hash генерируется автоматически при сохранении номенклатуры"
                  >
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.qr_hash !== curr.qr_hash}>
                      {() => {
                        const fullHash = formContext.getFieldValue("qr_hash");
                        const trimmedHash =
                          typeof fullHash === "string" && fullHash.includes("nm_")
                            ? fullHash.slice(fullHash.indexOf("nm_"))
                            : fullHash;

                        const handleCopy = async () => {
                          const textToCopy = trimmedHash || fullHash;
                          if (!textToCopy) {
                            setTimeout(() => {
                              messageApi.warning("Нет QR-hash для копирования");
                            }, 0);
                            return;
                          }

                          // Пытаемся через Clipboard API, при неудаче — fallback через textarea
                          const fallbackCopy = () => {
                            try {
                              const textarea = document.createElement("textarea");
                              textarea.value = textToCopy;
                              textarea.setAttribute("readonly", "");
                              textarea.style.position = "absolute";
                              textarea.style.left = "-9999px";
                              document.body.appendChild(textarea);
                              textarea.select();
                              const ok = document.execCommand("copy");
                              document.body.removeChild(textarea);
                              return ok;
                            } catch (err) {
                              return false;
                            }
                          };

                          try {
                            await navigator.clipboard.writeText(textToCopy);
                            setTimeout(() => {
                              messageApi.success("QR-hash скопирован");
                            }, 0);
                          } catch (err) {
                            const ok = fallbackCopy();
                            if (ok) {
                              setTimeout(() => {
                                messageApi.success("QR-hash скопирован");
                              }, 0);
                            } else {
                              setTimeout(() => {
                                messageApi.error("Не удалось скопировать QR-hash");
                              }, 0);
                              console.error("Clipboard copy failed:", err);
                            }
                          }
                        };

                        return (
                          <Input
                            disabled
                            value={trimmedHash}
                            placeholder="Автоматически генерируется при сохранении"
                            addonAfter={
                              <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={handleCopy}
                                disabled={!fullHash}
                                aria-label="Скопировать QR-hash"
                                style={{
                                  padding: "4px 8px",
                                  height: "auto",
                                  border: "none",
                                  boxShadow: "none",
                                }}
                              />
                            }
                          />
                        );
                      }}
                    </Form.Item>
                  </Form.Item>

                  <Form.Item
                    label={"Адрес"}
                    name={"address"}
                    tooltip="При выборе адреса широта и долгота заполняются автоматически"
                  >
                    <AutoComplete
                      options={optionsLocation}
                      onSearch={debounceOnChangeLocation}
                      onSelect={handleAddressSelect}
                      filterOption={false}
                      allowClear
                      placeholder="Введите адрес"
                    />
                  </Form.Item>

                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      marginBottom: "24px",
                    }}
                  >
                    <Form.Item
                      label={"Широта"}
                      name={"latitude"}
                      style={{ flex: 1, marginBottom: 0 }}
                      normalize={(value) =>
                        value === "" || value === null ? undefined : value
                      }
                      tooltip="Автоматически заполняется при выборе адреса"
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        step={0.000001}
                        placeholder="55.751244"
                        disabled
                      />
                    </Form.Item>

                    <Form.Item
                      label={"Долгота"}
                      name={"longitude"}
                      style={{ flex: 1, marginBottom: 0 }}
                      normalize={(value) =>
                        value === "" || value === null ? undefined : value
                      }
                      tooltip="Автоматически заполняется при выборе адреса"
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        step={0.000001}
                        placeholder="37.618423"
                        disabled
                      />
                    </Form.Item>
                  </div>
                </>
              ),
            },
          ]}
        />
        </Form>
      </>
    );
  },
);

Nomenclature.displayName = "Nomenclature";

export default Nomenclature;
